import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { OpaqueVideoData } from '@lumen5/luminary';
import Luminary from '@lumen5/luminary';
import { z } from 'zod';

import { ApiService, type Blueprint } from '../../client';
import type { EnhancedToolResult, ToolExecutionContext } from '../types';

import { describeReferenceVideo } from './reference-video-annotation';
import { buildReferenceStateGetter } from './reference-video-transcript';
import { registerReferenceScenes } from './scene-copy';
import { Tool } from './Tool';

import { getOrFetchBlueprint } from 'js/src/data/blueprints/blueprints-operations';
import { loadTemplateBundle } from 'js/src/data/templates/templates-operations';
import { getTemplateBundle } from 'js/src/data/templates/templates-selectors';
import { checkpointVideo } from 'js/src/data/video-data';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isLuminaryEmpty } from 'js/src/luminary/luminary-selectors';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary/utils';
import { isApiError } from 'js/src/types';

export const fetchReferenceVideoSchema = z.object({
    video_id: z
        .number()
        .optional()
        .describe(
            'ID of the video to use as the reference. Use when the user points at a specific video rather than a blueprint. Do not pass together with blueprint_id.',
        ),
    blueprint_id: z
        .number()
        .optional()
        .describe(
            'The blueprint whose reference video to fetch. When the user has selected a blueprint, this is provided in their preferences. Do not pass together with video_id.',
        ),
    apply_global_styles: z
        .boolean()
        .optional()
        .describe(
            "Copy the reference's video styling (template colors, fonts, watermark) to your video.",
        ),
});
export type FetchReferenceVideoArgs = z.infer<typeof fetchReferenceVideoSchema>;

/**
 * The reference's template settings, extracted the way `applyBlueprintTemplateSettingsOperation`
 * does it: load the video into a throwaway engine and save its template back out, so every setting
 * the template owns comes across rather than a hand-picked subset. Null when it has no template.
 */
function loadReferenceTemplate(
    referenceVideoData: OpaqueVideoData,
): ReturnType<Luminary['template']['save']> | null {
    const dataLuminary = new Luminary({
        fetchResources: false,
        disableRenderer: true,
        autoDraw: false,
        autoUpdate: false,
    });
    try {
        dataLuminary.load(referenceVideoData);
        return dataLuminary.template.id ? dataLuminary.template.save() : null;
    } catch (error) {
        ErrorReporter.capture(error, { fingerprint: ['fetch-reference-video-template-load'] });
        return null;
    } finally {
        dataLuminary.destroy();
    }
}

/** Let add_scene / add_voiceover_scene copy the reference's scenes by the scene_ids it shows. */
function makeReferenceScenesCopyable(referenceVideoData: OpaqueVideoData): void {
    const referenceTemplate = loadReferenceTemplate(referenceVideoData);
    if (referenceTemplate) {
        registerReferenceScenes(referenceVideoData, referenceTemplate as OpaqueVideoData);
    }
}

/** Distinguishes the reference's snapshot from the turn's own before/after video snapshots. */
const REFERENCE_SNAPSHOT_LABEL = 'Reference video';

const STYLING_ALREADY_APPLIED =
    "No change: the reference's template styling is already on your video.";

/**
 * Whether a styling outcome means the video now carries the reference's styling — freshly applied
 * or already there. `applyGlobalStyles` reports its failures in the same string it reports success
 * in, so the prefix is what distinguishes them.
 */
const stylingSucceeded = (styleOutcome: string): boolean =>
    styleOutcome.startsWith("Applied the reference's") || styleOutcome === STYLING_ALREADY_APPLIED;

/**
 * The annotation, unless this reference's annotation has already been served and the styling just
 * landed.
 *
 * The skill has the agent fetch the reference once to study it and again after creating the video
 * to copy its styling across; returning the full annotation both times duplicated tens of thousands
 * of tokens it was already holding. Suppression is keyed on having ACTUALLY served that reference
 * before, not merely on this being a styling call — agents routinely skip the study call and go
 * straight to `apply_global_styles`, and withholding the annotation there left them building a
 * look-alike of a video they had never seen.
 */
const bodyForStyleOutcome = (
    styleOutcome: string,
    annotation: string,
    alreadyServed: boolean,
): string =>
    alreadyServed && stylingSucceeded(styleOutcome)
        ? '\n\n_Annotation omitted — it is unchanged from your earlier fetch of this reference._'
        : `\n\n${annotation}`;

function formatBlueprintSettings(blueprint: Blueprint): string {
    const lines: string[] = [
        `- Default template: ${blueprint.default_template_id}`,
        `- Default aspect ratio: ${blueprint.default_aspect_ratio}`,
        `- Narrative thread: ${blueprint.narrative_thread}`,
        `- Target duration: ${blueprint.default_target_duration_seconds} seconds`,
    ];
    if (blueprint.ai_voice) {
        lines.push(`- AI voice: ${blueprint.ai_voice}`);
    }
    if (blueprint.description) {
        lines.push(`- Description: ${blueprint.description}`);
    }
    if (blueprint.supported_templates.length > 0) {
        lines.push(
            `- Templates with reference examples: ${blueprint.supported_templates.join(', ')}`,
        );
    }
    return lines.join('\n');
}

/**
 * Fetches a reference video — either a blueprint's example video or any video
 * the user can access — and returns its full annotation, so the agent can
 * build a new video that matches the reference's script style and visual
 * choices. The blueprint path reads the example through the blueprint-scoped
 * route, so the blueprint itself grants access to it, and additionally returns
 * the blueprint's settings and custom instructions.
 */
export class FetchReferenceVideoTool extends Tool<typeof fetchReferenceVideoSchema> {
    protected override readonly schema = fetchReferenceVideoSchema;
    protected override readonly toolName = TOOL_NAMES.fetchReferenceVideo;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription =
        "Fetch a reference video's complete scene-by-scene annotation (script text, scene designs, media, durations) to imitate its style. Pass blueprint_id when the user selected a blueprint (also returns the blueprint's settings and custom instructions); pass video_id to reference one of the user's own videos directly. Call this BEFORE building a video that should match an existing video's style.";

    /**
     * References whose annotation this tool has already returned, keyed per reference — a video by
     * its id, a blueprint example by the blueprint and template it came from, since that route
     * identifies the example rather than exposing its video id. The agent keeps its whole
     * transcript, so an annotation it was sent once is still in context — but only one it was
     * actually sent.
     */
    private readonly servedAnnotations = new Set<string>();

    public override getToolDisplayName(): string {
        return 'Studying reference video';
    }

    public get statusIcon(): string {
        return 'video_library';
    }

    public async execute(
        args: FetchReferenceVideoArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { video_id: videoId, blueprint_id: blueprintId } = args;

        if (videoId && blueprintId) {
            return {
                result: 'Error: Pass either video_id or blueprint_id, not both.',
                isSuccess: false,
            };
        }
        const applyStyles = args.apply_global_styles === true;
        const { parentActionGroup } = context;
        if (videoId) {
            return this.fetchByVideoId(videoId, applyStyles, parentActionGroup);
        }
        if (blueprintId) {
            return this.fetchByBlueprintId(blueprintId, applyStyles, parentActionGroup);
        }
        return {
            result: 'Error: Either video_id or blueprint_id is required.',
            isSuccess: false,
        };
    }

    private describeReference(
        videoData: OpaqueVideoData,
        transcripts?: Record<string, Record<string, unknown>>,
        templateBundle?: ReturnType<typeof getTemplateBundle>,
    ): Promise<string | null> {
        // Overlay the reference video's transcript editors onto the session state so its narration
        // renders with per-scene coverage and pauses — identical to get_video_state.
        const getState = buildReferenceStateGetter(this.getState(), transcripts);
        return describeReferenceVideo(videoData, getState, templateBundle);
    }

    /**
     * The reference's own template bundle, fetched the way opening a video does.
     *
     * The serializer replays the reference into a throwaway engine, and `load()` brings across only
     * the scene designs the video actually used — not the template's catalog — so without the
     * bundle no design resolves and every value the reference's author deliberately styled
     * serializes indistinguishably from one that just inherited the design's default. Null on any
     * failure: a template that can't be fetched costs those markers, never the annotation.
     */
    private async loadReferenceBundle(
        templateId: string | null | undefined,
    ): Promise<ReturnType<typeof getTemplateBundle>> {
        if (!templateId) {
            return null;
        }
        try {
            await this.dispatch(loadTemplateBundle(templateId));
            return getTemplateBundle(this.getState() as RootState, templateId);
        } catch (error) {
            // Not reported to Sentry: a reference can legitimately sit on a template this session
            // can't fetch (a prod-only one, a workspace the user lost access to), and the fallback
            // is benign — the annotation still goes out, only without the design-default markers.
            // The eval harness takes the same line for the same reason (see createToolboxAgent).
            console.warn(
                `[${TOOL_NAMES.fetchReferenceVideo}] could not load template bundle "${templateId}"; ` +
                    'the reference annotation will omit its design-default markers.',
                error,
            );
            return null;
        }
    }

    private async fetchAndDescribe(
        videoId: number,
    ): Promise<{ name: string; annotation: string | null; videoData: OpaqueVideoData }> {
        const videoResponse = await ApiService.videoRetrieve({
            path: { video_id: String(videoId) },
        });
        const video = videoResponse.data;
        const videoData = video.video_data as OpaqueVideoData;
        const templateBundle = await this.loadReferenceBundle(video.template_id);

        return {
            name: video.name,
            annotation: await this.describeReference(videoData, video.transcripts, templateBundle),
            videoData,
        };
    }

    /** A blueprint's example, fetched through the blueprint-scoped route. */
    private async fetchAndDescribeBlueprintExample(
        blueprintId: number,
        templateId: string,
    ): Promise<{ annotation: string | null; videoData: OpaqueVideoData }> {
        const response = await ApiService.blueprintsTemplateStyleVideoDataRetrieve({
            path: { id: blueprintId },
            query: { template: templateId },
        });
        // This route serializes video_data as a JSON string, unlike the per-video route.
        const videoData = JSON.parse(response.data.video_data) as OpaqueVideoData;
        const templateBundle = await this.loadReferenceBundle(templateId);

        return {
            annotation: await this.describeReference(
                videoData,
                response.data.transcripts,
                templateBundle,
            ),
            videoData,
        };
    }

    /**
     * Copy the reference's global styling onto the video being edited: its whole template (colors,
     * fonts, watermark).
     *
     * `template.load()` is the same call `applyBlueprintTemplateSettingsOperation` uses to put a
     * blueprint's styling on a video, so a reference video styles a video exactly the way a
     * blueprint does. Scenes, the outro included, are the agent's job: it copies them from the
     * reference by passing a reference scene_id to add_scene / add_voiceover_scene.
     */
    private applyGlobalStyles(
        referenceVideoData: OpaqueVideoData,
        parentActionGroup: string,
    ): string {
        // A template bundle, not just a scene, is required — `create_blank_video` produces one
        // with no scenes yet, and that is exactly when the agent should be styling. Guarding on
        // scenes (as an earlier version did) rejects the correct call and silently leaves the
        // video unstyled. Guarding on `template?.id` alone is not enough either: the id defaults
        // to a random uuid before any bundle is ever loaded, so it's truthy even when there is no
        // real template to style.
        if (!this.luminary.isTemplateBundleSet) {
            return (
                'Error: apply_global_styles needs a video to style — create the video first, then ' +
                'call this again with apply_global_styles to copy the reference styling onto it.'
            );
        }

        const applied: string[] = [];
        let templateAlreadyApplied = false;
        const referenceTemplate = loadReferenceTemplate(referenceVideoData);
        if (referenceTemplate) {
            // Compared through save() on both sides, so the reference is normalised the same way
            // the video's own template is rather than diffed against its raw serialized form.
            const templateBefore = JSON.stringify(this.luminary.template.save());
            this.luminary.template.load(referenceTemplate);
            templateAlreadyApplied =
                JSON.stringify(this.luminary.template.save()) === templateBefore;
            if (!templateAlreadyApplied) {
                applied.push('template styling');
            }
        }

        if (applied.length === 0) {
            return templateAlreadyApplied
                ? STYLING_ALREADY_APPLIED
                : 'Note: the reference carries no global styling to apply.';
        }
        const appliedSummary = `Applied the reference's ${applied.join(' and ')} to your video.${
            templateAlreadyApplied ? ' Its template styling was already on your video.' : ''
        }`;

        // The styling is already on the luminary; the checkpoint only records it for undo. A
        // scene-less video cannot be serialized at all (saveRawLuminary rejects it), and that is
        // the normal case here — styling right after create_blank_video. Whatever adds the first
        // scene checkpoints this change along with it.
        if (isLuminaryEmpty(this.luminary)) {
            return appliedSummary;
        }
        // Any other serialization failure (a video mid-edit) must not lose the styling or fail the
        // fetch either, so report it and carry on.
        try {
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );
        } catch (error) {
            ErrorReporter.capture(error, {
                fingerprint: ['fetch-reference-video-style-checkpoint'],
            });
        }
        return appliedSummary;
    }

    private async fetchByVideoId(
        videoId: number,
        applyStyles: boolean,
        parentActionGroup: string,
    ): Promise<EnhancedToolResult> {
        const { name, annotation, videoData } = await this.fetchAndDescribe(videoId);
        if (annotation === null) {
            return {
                result: `Error: Reference video "${name}" (id ${videoId}) has no scenes to imitate. Ask the user for a different reference video, or build without one.`,
                isSuccess: false,
            };
        }

        makeReferenceScenesCopyable(videoData);
        const styleOutcome = applyStyles
            ? this.applyGlobalStyles(videoData, parentActionGroup)
            : '';
        const styleNote = styleOutcome ? `\n\n${styleOutcome}` : '';
        const alreadyServed = this.servedAnnotations.has(`video:${videoId}`);
        this.servedAnnotations.add(`video:${videoId}`);

        return {
            result: `Success. Fetched reference video "${name}" (id ${videoId}).${styleNote}${bodyForStyleOutcome(styleOutcome, annotation, alreadyServed)}`,
            isSuccess: true,
            videoDataSnapshot: videoData,
            videoDataSnapshotLabel: REFERENCE_SNAPSHOT_LABEL,
        };
    }

    private async fetchByBlueprintId(
        blueprintId: number,
        applyStyles: boolean,
        parentActionGroup: string,
    ): Promise<EnhancedToolResult> {
        const blueprint = await this.dispatch(getOrFetchBlueprint(blueprintId));
        if (!blueprint) {
            return {
                result: `Error: Blueprint ${blueprintId} was not found or is not accessible.`,
                isSuccess: false,
            };
        }

        // The agent imitates the example's style, not its template, so any of the blueprint's
        // examples will do — take the default one, falling back to whichever exists. The fetch
        // selects the example by template, so what's needed here is the key rather than the id.
        const exampleTemplateId =
            blueprint.supported_templates_data[blueprint.default_template_id] !== undefined
                ? blueprint.default_template_id
                : Object.keys(blueprint.supported_templates_data)[0];
        if (exampleTemplateId === undefined) {
            return {
                result: `Error: Blueprint ${blueprintId} ("${blueprint.name}") has no reference video. Ask the user for a different reference`,
                isSuccess: false,
            };
        }

        let referenceAnnotation: string | null;
        let referenceVideoData: OpaqueVideoData;
        try {
            ({ annotation: referenceAnnotation, videoData: referenceVideoData } =
                await this.fetchAndDescribeBlueprintExample(blueprintId, exampleTemplateId));
        } catch (error) {
            // The blueprint route grants the example via the blueprint, so a refusal here means the
            // user isn't entitled to this blueprint's example at all (a workspace blueprint whose
            // example sits outside their projects) — report it rather than papering over it.
            if (isApiError(error) && (error.status === 403 || error.status === 404)) {
                ErrorReporter.capture(error, {
                    fingerprint: ['fetch-reference-video-blueprint-example-denied'],
                    extra: {
                        blueprintId,
                        exampleTemplateId,
                    },
                });
                return {
                    result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) could not be read. Ask the user for a different reference.`,
                    isSuccess: false,
                };
            }
            throw error;
        }

        if (referenceAnnotation === null) {
            return {
                result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) has no scenes to imitate. Ask the user for a different reference.`,
                isSuccess: false,
            };
        }

        const customInstructionsSection = blueprint.custom_instructions
            ? `\n\n## Custom Instructions (follow these)\n${blueprint.custom_instructions}`
            : '';
        // The blueprint route has no video id to key on, only the blueprint and its template.
        const exampleKey = `blueprint:${blueprintId}:${exampleTemplateId}`;
        const alreadyServed = this.servedAnnotations.has(exampleKey);
        this.servedAnnotations.add(exampleKey);
        makeReferenceScenesCopyable(referenceVideoData);
        const styleOutcome = applyStyles
            ? this.applyGlobalStyles(referenceVideoData, parentActionGroup)
            : '';
        const styleNote = styleOutcome ? `\n\n${styleOutcome}` : '';
        // The blueprint's settings and instructions are short and steer the build, so they stay on
        // the styling call; only the long annotation is dropped.
        const settingsSection = `\n\n## Blueprint Settings\n${formatBlueprintSettings(blueprint)}${customInstructionsSection}`;

        return {
            result: `Success. Fetched the reference video for blueprint "${blueprint.name}" (id ${blueprintId}).${styleNote}${settingsSection}${bodyForStyleOutcome(styleOutcome, referenceAnnotation, alreadyServed)}`,
            isSuccess: true,
            videoDataSnapshot: referenceVideoData,
            videoDataSnapshotLabel: REFERENCE_SNAPSHOT_LABEL,
        };
    }
}
